import React, { useEffect, useState, useContext } from "react";
import {
  Modal,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Fade,
  Typography,
  Backdrop,
  Alert,
  CircularProgress,
} from "@mui/material";
import Swal from "sweetalert2";
import {
  findReceiptFromAccountingSession,
  downloadReceiptPdf,
} from "../../Classes/API/ReceiptsApi";
import AccountingApi from "../../Classes/API/AccountingApi";
import CreateAccountingEntryReq from "../../Classes/Models/Requests/Accounting/CreateAccountingEntryReq";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { UserContext } from "App";
import { LanguageContext } from "App";
import { handlePrintReceipt } from "../../utils/receipts/receiptUtils";

const CustomModal = ({ open, onClose, size = "md", children }) => {
  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: size === "lg" ? 600 : 400,
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 2,
    p: 4,
    maxHeight: "90vh",
    overflowY: "auto",
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={open}>
        <Box sx={modalStyle}>{children}</Box>
      </Fade>
    </Modal>
  );
};

const AccountingEntryPopup = ({
  open,
  onClose,
  session,
  entryType = "AccountingSession",
  onRefresh,
  onSuccess,
}) => {
  const CurrentUser = useContext(UserContext);
  const { translations } = useContext(LanguageContext);

  const [paymentMethod, setPaymentMethod] = useState("");
  const [beneficiaryEntity, setBeneficiaryEntity] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [refundError, setRefundError] = useState("");
  const [receiptId, setReceiptId] = useState();

  const title = `Payment for Session: ${session.sessionName}`;
  const amount = session.finalPrice || 0;
  const category = "Accounting Sessions";
  const description =
    `Acc.Session Id: ${session.id}, Patient: ${session.patientName}, ` +
    `Original Price: EGP${session.originalPrice}, Discount: ${session.discount}%, ` +
    `Final Price: EGP${session.finalPrice}`;

  useEffect(() => {
    if (session && open) {
      setShowRefundForm(false);
      setRefundReason("");
      setRefundError("");
    }
  }, [session, open]);

  const handlePaymentMethodChange = (event) => {
    setPaymentMethod(event.target.value);
  };

  const handleRefundReasonChange = (event) => {
    setRefundReason(event.target.value);
    if (refundError) setRefundError("");
  };

  const validateRefund = () => {
    if (!refundReason.trim()) {
      setRefundError("Refund reason is required");
      return false;
    }
    if (refundReason.trim().length < 5) {
      setRefundError("Refund reason must be at least 5 characters");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!paymentMethod) {
      onClose();
      Swal.fire({
        title: "Error",
        text: "Payment Method is required",
        icon: "error",
        confirmButtonText: "Ok",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      let model = new CreateAccountingEntryReq();
      model.title = title;
      model.accountingSessionId = session?.id;
      model.creatorId = CurrentUser.id;
      model.amount = amount;
      model.category = category;
      model.description = description;
      model.entryType = entryType;
      model.paymentMethod = paymentMethod;
      model.beneficiaryEntity = beneficiaryEntity;

      let res = await AccountingApi.CreateAccountingEntry(model);

      if (res.status == "Success") {
        onClose();
        const result = await Swal.fire({
          title: "Success",
          text: "Payment processed successfully",
          icon: "success",
          showDenyButton: true,
          denyButtonText: "Print Receipt",
          confirmButtonText: "Ok",
          allowOutsideClick: false,
        });

        if (result.isConfirmed) {
          onClose();
        } else if (result.isDenied) {
          await handlePrintReceipt(session.id);
        }

        if (onSuccess) {
          onSuccess(res.data);
        }
        if (onRefresh) {
          onRefresh();
        }

        onClose();
      } else {
        Swal.fire({
          title: "Error",
          text: res.message,
          icon: "error",
          confirmButtonText: "Ok",
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "Failed to process payment",
        icon: "error",
        confirmButtonText: "Ok",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefund = async () => {
    if (!validateRefund()) return;

    setIsSubmitting(true);
    setRefundError("");

    try {
      let req = {
        AccSessionId: session.id,
        RefundReason: refundReason.trim(),
        RefundType: "AccountingSession",
        CreatorId: CurrentUser.id,
      };

      let res = await AccountingApi.RefundAccSession(req);

      if (res.status == "Success") {
        Swal.fire({
          title: "Success",
          text: "Refund processed successfully",
          icon: "success",
          confirmButtonText: "Ok",
          timer: 2000,
          timerProgressBar: true,
        });

        if (onSuccess) {
          onSuccess(res.data);
        }

        if (onRefresh) {
          onRefresh();
        }

        onClose();
      } else {
        onClose();
        Swal.fire({
          title: "Refund Failed",
          text: res.message || "Unable to process refund",
          icon: "error",
          confirmButtonText: "Ok",
          timer: 2000,
          timerProgressBar: true,
        });
      }
    } catch (error) {
      console.error("Refund error:", error);
      onClose();
      Swal.fire({
        title: "Error",
        text:
          error.response?.data?.message ||
          "Failed to process refund. Please try again.",
        icon: "error",
        confirmButtonText: "Ok",
        timer: 2000,
        timerProgressBar: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const startRefundProcess = () => {
    setShowRefundForm(true);
  };

  const cancelRefund = () => {
    setShowRefundForm(false);
    setRefundReason("");
    setRefundError("");
  };

  const getSessionStatusInfo = () => {
    if (session?.isAbsent) {
      return { message: "This session is marked as absent", type: "error" };
    }
    if (session?.isException) {
      return {
        message: "This is an exception session.",
        type: "warning",
      };
    }
    return null;
  };

  const statusInfo = getSessionStatusInfo();

  return (
    <CustomModal open={open} onClose={onClose} size="lg">
      <MDBox p={3}>
        <MDTypography variant="h5" gutterBottom>
          {session?.isPaidFor ? "Process Refund" : "Process Payment"}
        </MDTypography>

        {statusInfo && (
          <Alert severity={statusInfo.type} sx={{ mb: 2 }}>
            {statusInfo.message}
          </Alert>
        )}

        {session?.isPaidFor ? (
          // Refund UI
          <MDBox>
            <MDBox sx={{ mb: 3 }}>
              <MDTypography variant="body2" gutterBottom>
                <strong>Session:</strong> {session.sessionName}
              </MDTypography>
              <MDTypography variant="body2" gutterBottom>
                <strong>Patient:</strong> {session.patientName}
              </MDTypography>
              <MDTypography variant="body2" gutterBottom>
                <strong>Amount to refund:</strong> EGP{session.finalPrice}
              </MDTypography>
              <MDTypography variant="body2" gutterBottom>
                <strong>Original Price:</strong> EGP{session.originalPrice}
              </MDTypography>
              <MDTypography variant="body2" gutterBottom>
                <strong>Discount:</strong> {session.discount}%
              </MDTypography>
            </MDBox>

            {!showRefundForm ? (
              <MDBox>
                <MDButton
                  onClick={startRefundProcess}
                  color="warning"
                  disabled={isSubmitting || session?.isAbsent}
                  fullWidth
                  variant="contained"
                  size="large"
                >
                  Process Refund
                </MDButton>
                {session?.isAbsent && (
                  <MDTypography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Cannot refund absent sessions
                  </MDTypography>
                )}
              </MDBox>
            ) : (
              <MDBox>
                <TextField
                  label="Refund Reason *"
                  value={refundReason}
                  onChange={handleRefundReasonChange}
                  fullWidth
                  multiline
                  rows={3}
                  margin="normal"
                  error={!!refundError}
                  helperText={
                    refundError ||
                    "Please provide a detailed reason for the refund"
                  }
                  placeholder="Example: Patient cancelled appointment, duplicate payment, service not provided..."
                  autoFocus
                />

                <MDBox
                  mt={2}
                  display="flex"
                  justifyContent="space-between"
                  gap={1}
                >
                  <MDButton
                    onClick={cancelRefund}
                    color="secondary"
                    disabled={isSubmitting}
                    variant="outlined"
                  >
                    Cancel
                  </MDButton>
                  <MDButton
                    onClick={handleRefund}
                    color="warning"
                    disabled={isSubmitting || !refundReason.trim()}
                    variant="contained"
                  >
                    {isSubmitting ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Processing...
                      </>
                    ) : (
                      "Confirm Refund"
                    )}
                  </MDButton>
                </MDBox>
              </MDBox>
            )}
          </MDBox>
        ) : (
          // Payment UI
          <MDBox>
            <MDTypography variant="body2" gutterBottom>
              <strong>Session:</strong> {session?.sessionName}
            </MDTypography>
            <MDTypography variant="body2" gutterBottom>
              <strong>Patient:</strong> {session?.patientName}
            </MDTypography>
            <MDTypography variant="body2" gutterBottom>
              <strong>Final Price:</strong> EGP{session?.finalPrice}
            </MDTypography>

            <FormControl fullWidth margin="normal" variant="outlined">
              <InputLabel id="payment-method-label">
                Payment Method *
              </InputLabel>
              <Select
                labelId="payment-method-label"
                value={paymentMethod}
                onChange={handlePaymentMethodChange}
                label="Payment Method *"
                sx={{
                  height: 45,
                  cursor: "pointer",
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 2,
                    },
                  },
                }}
              >
                <MenuItem value="Visa">Visa</MenuItem>
                <MenuItem value="Cash">Cash</MenuItem>
                <MenuItem value="Bank-transfer">Bank Transfer</MenuItem>
              </Select>
            </FormControl>
            <MDBox mt={2} display="flex" justifyContent="space-between" gap={1}>
              <MDButton
                onClick={onClose}
                color="secondary"
                disabled={isSubmitting}
                variant="outlined"
              >
                Cancel
              </MDButton>
              <MDButton
                onClick={handleSubmit}
                color="success"
                disabled={isSubmitting || !paymentMethod}
                variant="contained"
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Processing...
                  </>
                ) : (
                  "Confirm Payment"
                )}
              </MDButton>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
    </CustomModal>
  );
};

export default AccountingEntryPopup;
